// Settings › Knowledge: what the AI is allowed to know. Prices, FAQs, pictures with captions,
// and free notes. Nothing else reaches the AI, so this page IS the AI's memory.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, assetUrl, type Asset, type KnowledgeItem, type KnowledgeKind } from '../../api';
import { Dialog } from '../../components/Dialog';
import { Loading, ErrorBox, Empty, InlineError } from '../../components/States';
import { useToast } from '../../components/Toast';

const TABS: { id: KnowledgeKind; label: string; hint: string; add: string }[] = [
  { id: 'price', label: 'Prices', hint: 'The AI only quotes prices that are on this list.', add: 'Add a price' },
  { id: 'faq', label: 'FAQs', hint: 'Questions customers ask, and the answer you want given.', add: 'Add a question' },
  { id: 'picture', label: 'Pictures', hint: 'Pictures the office can send, with a caption the AI reads (e.g. "RainSoft shut-off valve, under the sink, turn clockwise").', add: 'Add a picture' },
  { id: 'note', label: 'Notes', hint: 'Anything else the AI should know: hours, service area, how to book.', add: 'Add a note' },
];

export function KnowledgeSettings() {
  const [tab, setTab] = useState<KnowledgeKind>('price');
  const q = useQuery({ queryKey: ['knowledge', tab], queryFn: () => api.knowledge.list(tab) });
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<KnowledgeItem | 'new' | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api.knowledge.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge'] });
      toast({ text: 'Deleted.', tone: 'ok' });
    },
    onError: (e) => toast({ text: (e as Error).message, tone: 'error' }),
  });
  const meta = TABS.find((t) => t.id === tab)!;
  const items = (q.data?.items ?? []).filter((i) => i.kind === tab);

  return (
    <div className="card">
      <p className="muted small" style={{ marginBottom: 12 }}>
        The AI only knows what is on this page. If it is not here, the AI will not say it.
      </p>
      <div className="tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'active' : ''} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="row between" style={{ marginBottom: 8 }}>
        <span className="muted small">{meta.hint}</span>
        <button className="btn primary small" onClick={() => setEditing('new')}>
          {meta.add}
        </button>
      </div>
      {q.isPending && <Loading />}
      {q.isError && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      {q.isSuccess && items.length === 0 && <Empty title={`No ${meta.label.toLowerCase()} yet`} hint={meta.hint} />}
      {items.map((item) => (
        <div key={item.id} className="kb-item">
          <div>
            {item.kind === 'picture' && assetUrl(item.asset) && (
              <a href={assetUrl(item.asset)!} target="_blank" rel="noreferrer">
                <img src={assetUrl(item.asset)!} alt={item.title} />
              </a>
            )}
            <div>
              <strong>{item.title}</strong>
              {item.kind === 'price' && item.price_cents != null && <span className="price"> · ${(item.price_cents / 100).toFixed(2)}</span>}
              {item.active === false && <span className="chip" style={{ marginLeft: 6 }}>off</span>}
            </div>
            {item.body && <div className="body">{item.body}</div>}
            {item.kind === 'picture' && item.asset?.warning && <div className="tiny warn-text">{item.asset.warning}</div>}
          </div>
          <div className="row" style={{ alignItems: 'flex-start' }}>
            <button className="btn small" onClick={() => setEditing(item)}>
              Edit
            </button>
            <button className="btn small danger" onClick={() => confirm(`Delete “${item.title}”?`) && remove.mutate(item.id)} disabled={remove.isPending}>
              Delete
            </button>
          </div>
        </div>
      ))}
      {editing && <KnowledgeEditor kind={tab} item={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function KnowledgeEditor({ kind, item, onClose }: { kind: KnowledgeKind; item: KnowledgeItem | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState(item?.title ?? '');
  const [body, setBody] = useState(item?.body ?? '');
  const [dollars, setDollars] = useState(item?.price_cents != null ? (item.price_cents / 100).toFixed(2) : '');
  const [asset, setAsset] = useState<Asset | null>(item?.asset ?? null);
  const [assetId, setAssetId] = useState<string | null>(item?.asset_id ?? null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const priceCents = dollars.trim() ? Math.round(Number(dollars.replace(/[$,]/g, '')) * 100) : null;
  const priceBad = kind === 'price' && (priceCents == null || !Number.isFinite(priceCents) || priceCents < 0);

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setUploading(true);
    setUploadError(null);
    try {
      const r = await api.assets.upload(f);
      setAsset(r.asset);
      setAssetId(r.asset.id);
      if (!title.trim()) setTitle(f.name.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' '));
    } catch (e) {
      setUploadError((e as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const save = useMutation({
    mutationFn: () => {
      const p = {
        title: title.trim(),
        body: body.trim() || null,
        price_cents: kind === 'price' ? priceCents : null,
        asset_id: kind === 'picture' ? assetId : null,
      };
      return item ? api.knowledge.update(item.id, p) : api.knowledge.create({ kind, ...p });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge'] });
      toast({ text: 'Saved.', tone: 'ok' });
      onClose();
    },
  });

  const titleLabel = kind === 'faq' ? 'The question' : kind === 'picture' ? 'Caption (what the picture shows)' : kind === 'price' ? 'What it is' : 'Title';
  const bodyLabel = kind === 'faq' ? 'The answer' : kind === 'picture' ? 'When to send it (optional)' : kind === 'price' ? 'Details (optional: what is included, conditions)' : 'Text';
  const canSave = !!title.trim() && !priceBad && (kind !== 'faq' || !!body.trim()) && (kind !== 'note' || !!body.trim()) && (kind !== 'picture' || !!assetId) && !save.isPending && !uploading;

  return (
    <Dialog title={item ? `Edit ${kindWord(kind)}` : `Add ${kindWord(kind)}`} onClose={onClose} wide>
      {kind === 'picture' && (
        <div className="field">
          <span className="field-label">Picture</span>
          {asset && assetUrl(asset) && <img src={assetUrl(asset)!} alt="" style={{ maxWidth: 200, maxHeight: 160, borderRadius: 8, display: 'block', marginBottom: 6 }} />}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(e) => void onFile(e.target.files?.[0])} disabled={uploading} />
          {uploading && <div className="muted small">Uploading</div>}
          {asset?.warning && <div className="tiny warn-text">{asset.warning}</div>}
          {uploadError && <div className="tiny danger-text">{uploadError}</div>}
          <div className="field-help">PNG, JPG or WEBP, up to 10 MB. Keep it under 1 MB so it sends as MMS without trouble.</div>
        </div>
      )}
      <label className="field">
        <span>{titleLabel}</span>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus={kind !== 'picture'} placeholder={kind === 'faq' ? 'Do you service other brands?' : kind === 'price' ? 'Annual service visit' : kind === 'picture' ? 'RainSoft shut-off valve, under the sink, turn clockwise' : 'Office hours'} />
      </label>
      {kind === 'price' && (
        <label className="field">
          <span>Price (dollars)</span>
          <input className="input" value={dollars} onChange={(e) => setDollars(e.target.value)} placeholder="149.00" inputMode="decimal" style={{ maxWidth: 160 }} />
          {priceBad && dollars && <div className="tiny danger-text">Enter a number like 149 or 149.00.</div>}
        </label>
      )}
      <label className="field">
        <span>{bodyLabel}</span>
        <textarea className="input" value={body} onChange={(e) => setBody(e.target.value)} rows={kind === 'note' || kind === 'faq' ? 5 : 3} />
      </label>
      <InlineError error={save.error} />
      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={!canSave} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving' : 'Save'}
        </button>
      </div>
    </Dialog>
  );
}

function kindWord(k: KnowledgeKind): string {
  return k === 'faq' ? 'a question' : k === 'price' ? 'a price' : k === 'picture' ? 'a picture' : 'a note';
}
